/*
 * LawFirmSeoScorecard
 * Twelve yes / no / not sure questions, two for each of six areas of law firm SEO.
 *
 * Rules
 *   Each area gets: 2 points for each "yes", 1 for "not sure", 0 for "no". Area totals run from 0 to 4.
 *   Area status: 4 = "Strong", 2 or 3 = "Needs work", 0 or 1 = "Weak".
 *   Results show once all 12 questions are answered:
 *     - areas listed weakest first (ties keep the order below), each with what to do and a link to the related service page
 *     - overall message: any "Weak" area -> start with the weakest area;
 *       otherwise any "Needs work" -> fix those before adding more content;
 *       otherwise -> foundations are strong, focus on content and links in competitive practice areas
 *   This is a self-check, not a score against other firms. No benchmarks, tracking, storage or network calls.
 */
import { useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

type Answer = "yes" | "no" | "unsure";
type AreaKey = "technical" | "pages" | "local" | "content" | "links" | "tracking";
type Area = { key: AreaKey; name: string; fix: string; href: string; linkText: string };
type Q = { id: string; area: AreaKey; q: string };

const AREAS: Area[] = [
  { key: "technical", name: "Technical SEO", fix: "Fix crawling, indexing and mobile speed first. Everything else depends on it.", href: "/services/technical-seo-for-law-firms", linkText: "Technical SEO for law firms" },
  { key: "pages", name: "Practice pages", fix: "Give every practice area and main case type its own page that answers what clients ask.", href: "/services/legal-content-writing", linkText: "Legal content writing" },
  { key: "local", name: "Local SEO", fix: "Complete your Google Business Profile, ask every happy client for a review and fix your listings.", href: "/services/local-seo-for-law-firms", linkText: "Local SEO for law firms" },
  { key: "content", name: "Content", fix: "Publish helpful answers to the questions clients ask before they call, approved by your attorneys.", href: "/services/legal-content-writing", linkText: "Legal content writing" },
  { key: "links", name: "Links and authority", fix: "Earn links from legal, local and news sites, and check for risky links from past agencies.", href: "/services/link-building-for-law-firms", linkText: "Link building for law firms" },
  { key: "tracking", name: "Tracking", fix: "Track calls, forms and signed cases by source, so you can see what SEO is really worth.", href: "/services/law-firm-marketing-analytics", linkText: "Law firm marketing analytics" },
];

const QUESTIONS: Q[] = [
  { id: "t1", area: "technical", q: "Do your pages load quickly on a phone?" },
  { id: "t2", area: "technical", q: "Are all your important pages indexed in Google Search Console?" },
  { id: "p1", area: "pages", q: "Does each practice area have its own page, not one shared page?" },
  { id: "p2", area: "pages", q: "Do your practice pages mention the cities you serve and make calling easy?" },
  { id: "l1", area: "local", q: "Is your Google Business Profile complete, with the right categories and hours?" },
  { id: "l2", area: "local", q: "Do you get new Google reviews every month?" },
  { id: "c1", area: "content", q: "Have you published or updated helpful content in the last three months?" },
  { id: "c2", area: "content", q: "Does an attorney at your firm review what's published?" },
  { id: "k1", area: "links", q: "Do legal, local or news sites link to your website?" },
  { id: "k2", area: "links", q: "Are you sure no past agency built spammy or paid links to your site?" },
  { id: "r1", area: "tracking", q: "Do you track phone calls from your website by source?" },
  { id: "r2", area: "tracking", q: "Can you see which signed cases came from search?" },
];

const OPTIONS: { value: Answer; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Not sure" },
];

const POINTS: Record<Answer, number> = { yes: 2, unsure: 1, no: 0 };

function status(total: number): "Strong" | "Needs work" | "Weak" {
  if (total >= 4) return "Strong";
  if (total >= 2) return "Needs work";
  return "Weak";
}

export default function LawFirmSeoScorecard() {
  const uid = useId();
  const [answers, setAnswers] = useState<Record<string, Answer | undefined>>({});
  const answered = QUESTIONS.filter((q) => answers[q.id]).length;
  const done = answered === QUESTIONS.length;

  const results = AREAS.map((a, i) => {
    const total = QUESTIONS.filter((q) => q.area === a.key).reduce((sum, q) => sum + (answers[q.id] ? POINTS[answers[q.id] as Answer] : 0), 0);
    return { ...a, total, status: status(total), order: i };
  }).sort((x, y) => x.total - y.total || x.order - y.order);

  let overall = "";
  if (done) {
    if (results.some((r) => r.status === "Weak")) overall = `Start with ${results[0].name.toLowerCase()}. It's your weakest area and it's holding the rest back.`;
    else if (results.some((r) => r.status === "Needs work")) overall = "No area is weak, but some need work. Fix those before adding more content.";
    else overall = "Your foundations look strong. The next gains come from content and links in your most competitive practice areas.";
  }

  const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-lg border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Law firm SEO scorecard</h3>
      <p className="mt-1 text-sm text-muted-foreground">Twelve quick questions. A self-check, not a comparison with other firms. Nothing is saved or sent anywhere.</p>

      <ol className="mt-5 space-y-3">
        {QUESTIONS.map((q, i) => {
          const labelId = `${uid}-${q.id}`;
          const areaName = AREAS.find((a) => a.key === q.area)?.name;
          return (
            <li key={q.id} className="rounded-lg border border-border p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{areaName}</p>
              <p id={labelId} className="mt-1 text-sm font-medium">{i + 1}. {q.q}</p>
              <div role="group" aria-labelledby={labelId} className="mt-3 flex flex-wrap gap-2">
                {OPTIONS.map((o) => {
                  const pressed = answers[q.id] === o.value;
                  return (
                    <Button
                      key={o.value}
                      type="button"
                      variant={pressed ? "default" : "outline"}
                      aria-pressed={pressed}
                      onClick={() => setAnswers((p) => ({ ...p, [q.id]: o.value }))}
                      className={`rounded-md border px-4 py-1.5 text-sm ${focusRing} ${
                        pressed ? "border-border bg-primary text-primary-foreground" : "border-input bg-card text-card-foreground"
                      }`}
                    >
                      {o.label}
                    </Button>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ol>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4 text-sm">
        {!done ? (
          <p className="text-muted-foreground">
            {QUESTIONS.length - answered} question{QUESTIONS.length - answered === 1 ? "" : "s"} left before your results.
          </p>
        ) : (
          <div>
            <p className="font-semibold">{overall}</p>
            <ul className="mt-3 space-y-2">
              {results.map((r) => (
                <li key={r.key} className="rounded-md border border-border bg-card p-3">
                  <p className="font-medium">
                    {r.name}: {r.status}
                  </p>
                  {r.status !== "Strong" && (
                    <p className="mt-1 text-muted-foreground">
                      {r.fix}{" "}
                      <Link to={r.href} className={`font-medium text-card-foreground underline underline-offset-4 ${focusRing}`}>
                        {r.linkText}
                      </Link>
                    </p>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-4">
              Want an expert view of your site?{" "}
              <a href="#free-plan" className={`font-medium underline underline-offset-4 ${focusRing}`}>Get a free law firm SEO audit →</a>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
